/**
 * Supabase Sync Hook
 * 
 * Provides a hook for the legacy Supabase/bootstrap sync path.
 * Keep comments here aligned with actual call sites before treating it as the active app bootstrap.
 */

import { useEffect, useRef, useState } from 'react';
import { isSupabaseAvailable, getCurrentWorkspaceId, isInDemoMode } from '../lib/supabaseSync';
import { useItemsStore } from '../stores/prioritization/itemsStore';
import { useDegapProcessStore } from '../stores/prioritization/degapProcessStore';
import { useSessionStore } from '../stores/prioritization/sessionStore';
import { useItemPoolsStore } from '../stores/prioritization/itemPoolsStore';
import { useClusterStore } from '../stores/prioritization/clusterStore';
import { useAttributesStore } from '../stores/prioritization/attributesStore';
import { useEnterpriseIdentityStore } from '../stores/prioritization/enterpriseIdentityStore';
import { useCatchballLoopStore } from '../stores/prioritization/catchballLoopStore';
import { usePrioritySetStore } from '../stores/prioritization/prioritySetStore';
import { useCycleParticipationStore } from '../stores/prioritization/cycleParticipationStore';
import { useArrangeStore } from '../stores/prioritization/arrangeStore';
import { useUIStore } from '../stores/prioritization/uiStore';
import { useTenantStore } from '../stores/prioritization/tenantStore';
import { migrateToSupabase, isMigrationComplete } from '../utils/migrateToSupabase';

/**
 * Wait for persisted Zustand stores to rehydrate before the legacy migration/sync path runs.
 * This only exists for compatibility with older persisted client state.
 */
async function waitForRehydration(): Promise<void> {
  // Give Zustand persist middleware time to rehydrate
  // The persist middleware typically rehydrates synchronously, but we add
  // a small delay to ensure all stores have completed
  return new Promise((resolve) => {
    // Check if stores have data (indicates rehydration complete)
    const checkRehydration = () => {
      // Use requestAnimationFrame to ensure React has finished its render cycle
      requestAnimationFrame(() => {
        setTimeout(resolve, 50); // Small additional delay for safety
      });
    };
    
    // If document is already loaded, check immediately
    if (document.readyState === 'complete') {
      checkRehydration();
    } else {
      // Wait for page load
      window.addEventListener('load', checkRehydration, { once: true });
    }
  });
}

interface SyncStatus {
  isInitialized: boolean;
  isLoading: boolean;
  isMigrating: boolean;
  error: string | null;
  supabaseAvailable: boolean;
}

/**
 * Hook to initialize Supabase sync for all stores.
 * 
 * This hook:
 * 1. Checks if Supabase is available
 * 2. Runs one-time migration from persisted client state if needed
 * 3. Fetches all data from Supabase into stores
 * 
 * @returns Sync status object
 */
export function useSupabaseSync(): SyncStatus {
  const [status, setStatus] = useState<SyncStatus>({
    isInitialized: false,
    isLoading: false,
    isMigrating: false,
    error: null,
    supabaseAvailable: false,
  });
  
  const initRef = useRef(false);
  const hasRunSyncRef = useRef(false);
  const lastSyncedWorkspaceIdRef = useRef<string | null>(null);
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  
  // Get fetch functions from stores
  const fetchItems = useItemsStore(state => state.fetchFromSupabase);
  const fetchCycles = useDegapProcessStore(state => state.fetchFromSupabase);
  const fetchSessions = useSessionStore(state => state.fetchFromSupabase);
  const fetchItemPools = useItemPoolsStore(state => state.fetchFromSupabase);
  const fetchClusters = useClusterStore(state => state.fetchFromSupabase);
  const fetchAttributes = useAttributesStore(state => state.fetchFromSupabase);
  const fetchOrgUnits = useEnterpriseIdentityStore(state => state.fetchFromSupabase);
  const fetchCatchballLoops = useCatchballLoopStore(state => state.fetchFromSupabase);
  const fetchPrioritySets = usePrioritySetStore(state => state.fetchFromSupabase);
  const fetchCycleParticipation = useCycleParticipationStore(state => state.fetchFromSupabase);
  const fetchArrangeSnapshots = useArrangeStore(state => state.fetchFromSupabase);
  const fetchUIPreferences = useUIStore(state => state.fetchPreferencesFromSupabase);
  
  useEffect(() => {
    // Only run once
    if (initRef.current) return;
    initRef.current = true;
    
    const initialize = async () => {
      // Wait for persisted Zustand state before running the legacy migration/sync path.
      console.log('[SupabaseSync] Waiting for persisted store rehydration...');
      await waitForRehydration();
      console.log('[SupabaseSync] Rehydration complete, proceeding with Supabase sync');
      
      // Check if Supabase is available
      const supabaseAvailable = isSupabaseAvailable();
      
      if (!supabaseAvailable) {
        console.log('[SupabaseSync] Supabase not available; legacy client-only state remains in effect');
        setStatus({
          isInitialized: true,
          isLoading: false,
          isMigrating: false,
          error: null,
          supabaseAvailable: false,
        });
        return;
      }
      
      setStatus(prev => ({ ...prev, supabaseAvailable: true, isLoading: true }));
      
      try {
        // Run migration if needed
        if (!isMigrationComplete()) {
          console.log('[SupabaseSync] Running one-time migration...');
          setStatus(prev => ({ ...prev, isMigrating: true }));
          
          const migrationResult = await migrateToSupabase();
          
          if (!migrationResult.success) {
            console.warn('[SupabaseSync] Migration had errors:', migrationResult.errors);
          }
          
          setStatus(prev => ({ ...prev, isMigrating: false }));
        }
        
        const workspaceId = getCurrentWorkspaceId();
        const inDemoMode = isInDemoMode();
        if (!workspaceId && !inDemoMode) {
          console.log('[SupabaseSync] No workspace selected and not in demo mode; skipping data fetch. Loading org units so Submit Idea queue list can show when workspace is selected.');
          await fetchOrgUnits();
          setStatus({
            isInitialized: true,
            isLoading: false,
            isMigrating: false,
            error: null,
            supabaseAvailable: true,
          });
          return;
        }
        
        // Fetch in batches to avoid AbortError from too many concurrent requests / default timeouts
        console.log('[SupabaseSync] Fetching data from Supabase (batched)...');
        const batch1 = [fetchItems, fetchCycles, fetchSessions, fetchItemPools];
        const batch2 = [fetchClusters, fetchAttributes, fetchOrgUnits, fetchCatchballLoops];
        const batch3 = [fetchPrioritySets, fetchCycleParticipation, fetchArrangeSnapshots, fetchUIPreferences];
        await Promise.all(batch1.map((f) => f()));
        await Promise.all(batch2.map((f) => f()));
        await Promise.all(batch3.map((f) => f()));
        
        hasRunSyncRef.current = true;
        lastSyncedWorkspaceIdRef.current = workspaceId ?? null;
        console.log('[SupabaseSync] Data sync complete');
        
        setStatus({
          isInitialized: true,
          isLoading: false,
          isMigrating: false,
          error: null,
          supabaseAvailable: true,
        });
        
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        console.error('[SupabaseSync] Error during initialization:', error);
        
        setStatus({
          isInitialized: true,
          isLoading: false,
          isMigrating: false,
          error: message,
          supabaseAvailable: true,
        });
      }
    };
    
    initialize();
  }, [
    fetchItems,
    fetchCycles,
    fetchSessions,
    fetchItemPools,
    fetchClusters,
    fetchAttributes,
    fetchOrgUnits,
    fetchCatchballLoops,
    fetchPrioritySets,
    fetchCycleParticipation,
    fetchArrangeSnapshots,
    fetchUIPreferences,
  ]);

  // When user selects or switches workspace after load, run sync for the new workspace
  useEffect(() => {
    if (!status.isInitialized || !currentWorkspaceId || currentWorkspaceId === lastSyncedWorkspaceIdRef.current) {
      return;
    }
    const runSync = async () => {
      console.log('[SupabaseSync] Workspace selected, fetching data for workspace:', currentWorkspaceId);
      try {
        const batch1 = [fetchItems, fetchCycles, fetchSessions, fetchItemPools];
        const batch2 = [fetchClusters, fetchAttributes, fetchOrgUnits, fetchCatchballLoops];
        const batch3 = [fetchPrioritySets, fetchCycleParticipation, fetchArrangeSnapshots, fetchUIPreferences];
        await Promise.all(batch1.map((f) => f()));
        await Promise.all(batch2.map((f) => f()));
        await Promise.all(batch3.map((f) => f()));
        hasRunSyncRef.current = true;
        lastSyncedWorkspaceIdRef.current = currentWorkspaceId;
        console.log('[SupabaseSync] Data sync complete for workspace');
      } catch (e) {
        console.error('[SupabaseSync] Error syncing after workspace change:', e);
      }
    };
    runSync();
  }, [
    status.isInitialized,
    currentWorkspaceId,
    fetchItems,
    fetchCycles,
    fetchSessions,
    fetchItemPools,
    fetchClusters,
    fetchAttributes,
    fetchOrgUnits,
    fetchCatchballLoops,
    fetchPrioritySets,
    fetchCycleParticipation,
    fetchArrangeSnapshots,
    fetchUIPreferences,
  ]);

  return status;
}

/**
 * Hook to get aggregate sync state across all stores
 */
export function useSyncStatus() {
  const itemsSyncState = useItemsStore(state => state.syncState);
  const cyclesSyncState = useDegapProcessStore(state => state.syncState);
  const sessionsSyncState = useSessionStore(state => state.syncState);
  const itemPoolsSyncState = useItemPoolsStore(state => state.syncState);
  const clustersSyncState = useClusterStore(state => state.syncState);
  const attributesSyncState = useAttributesStore(state => state.syncState);
  
  const isAnyLoading = 
    itemsSyncState.isLoading ||
    cyclesSyncState.isLoading ||
    sessionsSyncState.isLoading ||
    itemPoolsSyncState.isLoading ||
    clustersSyncState.isLoading ||
    attributesSyncState.isLoading;
  
  const isAnySyncing =
    itemsSyncState.isSyncing ||
    cyclesSyncState.isSyncing ||
    sessionsSyncState.isSyncing ||
    itemPoolsSyncState.isSyncing ||
    clustersSyncState.isSyncing ||
    attributesSyncState.isSyncing;
  
  const errors = [
    itemsSyncState.syncError,
    cyclesSyncState.syncError,
    sessionsSyncState.syncError,
    itemPoolsSyncState.syncError,
    clustersSyncState.syncError,
    attributesSyncState.syncError,
  ].filter(Boolean);
  
  return {
    isLoading: isAnyLoading,
    isSyncing: isAnySyncing,
    hasErrors: errors.length > 0,
    errors,
  };
}
